<template>
  <div class="colleague-invite-field">
    <label class="colleague-invite-field__label" :for="inputId">
      {{ $t("components.inviteColleagues.field_label") }}
    </label>
    <!--
      Figma 6969:62555 / 6969:62713 / 6971:74518, gebouwd op de Vuetify-combobox
      met chips (de annotatie bij het ontwerp). Een collega kies je uit de lijst,
      een adres zonder account typ je in: dat wordt een e-mailchip.
    -->
    <v-combobox
      ref="combobox"
      :id="inputId"
      :model-value="modelValue"
      @update:model-value="onSelect"
      v-model:search="search"
      v-model:menu="menu"
      :items="results"
      item-title="name"
      item-value="key"
      return-object
      multiple
      chips
      no-filter
      :hide-no-data="false"
      :clear-on-select="true"
      variant="solo"
      menu-icon="mdi-menu-down"
      hide-details="auto"
      :autofocus="autofocus"
      :placeholder="emailMode ? $t('components.inviteColleagues.email_mode_placeholder') : undefined"
      :error-messages="errorMessages"
      :menu-props="{
        contentClass: 'colleague-invite-menu colleague-invite-menu--panel',
        maxHeight: 360,
        maxWidth: menuWidth,
      }"
      class="colleague-invite-field__input"
    >
      <template #chip="{ props, item }">
        <v-chip
          v-bind="props"
          class="colleague-invite-chip"
          :class="{ 'colleague-invite-chip--invalid': isInvalid(item.raw) }"
          closable
          close-icon="mdi-close-circle"
        >
          {{ item.raw.name || item.raw }}
        </v-chip>
      </template>

      <template #item="{ props, item }">
        <v-list-item
          v-bind="props"
          :title="undefined"
          class="colleague-invite-option"
        >
          <div class="colleague-invite-option__row">
            <v-icon class="colleague-invite-option__check">
              {{ isSelected(item.raw) ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
            </v-icon>
            <span class="colleague-invite-option__name">{{ item.raw.name }}</span>
          </div>
        </v-list-item>
      </template>

      <template #no-data>
        <div class="colleague-invite-menu__body">
          <div v-if="loading" class="colleague-invite-menu__hint">
            {{ $t("components.inviteColleagues.searching") }}
          </div>
          <!-- Figma 6971:74542: wat je typt, als e-mailchip om aan te klikken. -->
          <button
            v-else-if="term.length >= MIN_CHARS"
            type="button"
            class="colleague-invite-menu__email"
            @mousedown.prevent
            @click="addEmail(term)"
          >
            <span class="colleague-invite-menu__email-label">
              {{ $t("components.inviteColleagues.email_row") }}
            </span>
            <span class="colleague-invite-chip colleague-invite-chip--static">{{ term }}</span>
          </button>
          <template v-else>
            <div v-if="atMax" class="colleague-invite-menu__hint">
              {{ $t("components.inviteColleagues.max", { max: MAX_RECIPIENTS }) }}
            </div>
            <!--
              Na "Nieuwe collega uitnodigen" staat hier wat je nu doet, niet
              dezelfde knop nog een keer: anders leek de klik niets te doen
              (hertest 1240ccep2j1).
            -->
            <div v-else-if="emailMode" class="colleague-invite-menu__hint">
              {{ $t("components.inviteColleagues.email_mode_hint") }}
            </div>
            <button
              v-else
              type="button"
              class="colleague-invite-menu__new"
              @mousedown.prevent
              @click="startEmail"
            >
              <span class="colleague-invite-menu__new-icon"><v-icon size="24">mdi-plus</v-icon></span>
              {{ $t("components.inviteColleagues.new_colleague") }}
            </button>
          </template>
        </div>
      </template>

      <template #append-item>
        <div v-if="results.length && !atMax" class="colleague-invite-menu__footer">
          <button
            type="button"
            class="colleague-invite-menu__new"
            @mousedown.prevent
            @click="startEmail"
          >
            <span class="colleague-invite-menu__new-icon"><v-icon size="24">mdi-plus</v-icon></span>
            {{ $t("components.inviteColleagues.new_colleague") }}
          </button>
        </div>
      </template>
    </v-combobox>
  </div>
</template>

<script>
import { debounce } from "lodash";
import api from "@/services/api";
import { useMainStore } from "@/stores/store";
import { isEmailAddress } from "@/util/emailAddress";

// De collegakiezer zoekt vanaf twee tekens (ADR-0071); de lijst toont er vijf
// (ticket 86c47j3zm) en één verzoek nodigt er hooguit tien uit.
const MIN_CHARS = 2;
const MAX_RESULTS = 5;
const MAX_RECIPIENTS = 10;

let uid = 0;

/**
 * De ontvangers van een Collega-uitnodiging: collega's met een account
 * (`{key, id, name}`) en adressen zonder (`{key, email, name}`). De waarde is
 * die lijst; `toInvitePayload()` (util/inviteOutcome) maakt er de
 * `colleagues`/`emails` van die de API leest.
 */
export default {
  emits: ["update:modelValue"],
  props: {
    modelValue: { type: Array, default: () => [] },
    // Adressen die de server weigerde (400 `errors`): hun chip kleurt rood.
    invalidEmails: { type: Array, default: () => [] },
    error: { type: String, default: "" },
    autofocus: { type: Boolean, default: false },
  },
  data: () => ({
    MIN_CHARS,
    MAX_RECIPIENTS,
    inputId: `colleague-invite-${++uid}`,
    search: "",
    menu: false,
    results: [],
    loading: false,
    seq: 0,
    emailMode: false,
    localError: "",
    // Het menu is zo breed als het veld: Vuetify geeft het veld alleen als
    // minimum, en op een smal scherm duwde "Nieuwe collega uitnodigen" het menu
    // er voorbij (staging-review 8 okt 2026: 7px op 375, 46px op 320).
    menuWidth: undefined,
    mainstore: useMainStore(),
  }),
  computed: {
    term() {
      return (this.search || "").trim();
    },
    atMax() {
      return this.modelValue.length >= MAX_RECIPIENTS;
    },
    errorMessages() {
      return [this.localError || this.error].filter(Boolean);
    },
  },
  watch: {
    menu(open) {
      if (!open) return;
      const width = this.$el?.querySelector?.(".v-field")?.offsetWidth;
      if (width) this.menuWidth = width;
    },
    search(value) {
      this.localError = "";
      const term = (value || "").trim();
      if (term.length < MIN_CHARS || this.atMax) {
        this.debouncedFetch.cancel();
        // Een antwoord dat nog onderweg is, telt niet meer.
        this.seq += 1;
        this.results = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      this.debouncedFetch(term);
    },
  },
  created() {
    // Zoekt terwijl je typt, maar niet bij elke toetsaanslag.
    this.debouncedFetch = debounce(this.fetch, 300);
  },
  beforeUnmount() {
    this.debouncedFetch.cancel();
  },
  methods: {
    /** Collega's van deze pagina op naam; alleen het laatste antwoord telt. */
    fetch(term) {
      const seq = ++this.seq;
      api
        .get(`/v1/pages/${this.mainstore.companypage.domain}/colleagues`, {
          params: { search: term },
        })
        .then((res) => {
          if (seq !== this.seq) return;
          this.results = (res.data.colleagues || []).slice(0, MAX_RESULTS).map((colleague) => ({
            key: `u:${colleague.id}`,
            id: colleague.id,
            name: colleague.name,
          }));
        })
        .catch(() => {
          if (seq === this.seq) this.results = [];
        })
        .finally(() => {
          if (seq === this.seq) this.loading = false;
        });
    },

    /**
     * De combobox geeft gekozen collega's als object en ingetypte tekst als
     * string. Een string die een e-mailadres is wordt een e-mailchip; andere
     * tekst valt weg -- een naam die niet in de lijst stond is niemand.
     */
    onSelect(values) {
      const next = [];
      let rejected = false;
      for (const value of values || []) {
        if (value && typeof value === "object") {
          next.push(value.raw && value.raw.key ? value.raw : value);
          continue;
        }
        const text = String(value || "").trim().toLowerCase();
        // Dezelfde grens als de server, die elk adres met filter_var toetst.
        if (isEmailAddress(text)) {
          next.push({ key: `e:${text}`, email: text, name: text });
        } else if (text.includes("@")) {
          rejected = true;
        }
      }
      this.emit(next);
      this.localError = rejected ? this.$t("components.inviteColleagues.not_an_email") : "";
      if (!rejected) this.emailMode = false;
    },

    addEmail(text) {
      const email = text.trim().toLowerCase();
      if (!isEmailAddress(email)) {
        this.localError = this.$t("components.inviteColleagues.not_an_email");
        return;
      }
      this.emit([...this.modelValue, { key: `e:${email}`, email, name: email }]);
      this.clearSearch();
      this.emailMode = false;
    },

    startEmail() {
      this.emailMode = true;
      this.clearSearch();
      this.$nextTick(() => this.$refs.combobox?.focus());
    },

    // Vuetify's combobox houdt zijn zoektekst zelf bij en leest `v-model:search`
    // niet terug; leegmaken gaat via de search die hij exposet.
    clearSearch() {
      this.search = "";
      if (this.$refs.combobox) this.$refs.combobox.search = "";
    },

    emit(items) {
      // Elk één keer, en nooit meer dan het maximum.
      const seen = new Set();
      const unique = items.filter((item) => item && !seen.has(item.key) && seen.add(item.key));
      this.$emit("update:modelValue", unique.slice(0, MAX_RECIPIENTS));
    },

    isSelected(raw) {
      return this.modelValue.some((item) => item.key === raw?.key);
    },

    isInvalid(raw) {
      return !!raw?.email && this.invalidEmails.includes(raw.email);
    },

    focus() {
      this.$refs.combobox?.focus();
    },
  },
};
</script>
